import PropTypes from 'prop-types';
import _ from 'lodash';

/**
 * This function returns a [custom validator](https://reactjs.org/docs/typechecking-with-proptypes.html)
 * that can be used to check against a child components prop. In essence, this function allows you
 * to do the safe equivalent of the following
 *```javascript
 *import ChildComponent from 'ChildComponent';
 *
 * export default function MyComponent(props) {
 *     return (
 *         <div>
 *             <MyComponent abc={props.abc} />
 *         </div>
 *     );
 * }
 *
 * MyComponent.propTypes = {
 *     abc: ChildComponent.propTypes.abc // This pattern is what this function replaces
 * }
 * ```
 *
 * The custom validator returned by this function first tries to use the propType specified by
 * the ChildComponent, but if it's undefined, defaults to just passing the prop validation.
 * @param {React.Component} Component - Component whose PropType we want to actually use
 * @param {String} overridePropName - Override which PropType of {@link Component} to check against
 * @example
 * import React from 'react';
 * import PropTypes from 'prop-types';
 *
 * function ChildComponent({ abc }) {
 *     return <span>{abc}</span>;
 * }
 *
 * ChildComponent.propTypes = {
 *     abc: PropTypes.string.isRequired,
 *     def: PropTypes.number.isRequired,
 * };
 *
 * export default function MyComponent(props) {
 *     return (
 *         <div>
 *             <MyComponent abc={props.abc} def={props.differentNameForDef} />
 *         </div>
 *     );
 * }
 *
 * MyComponent.propTypes = {
 *     abc: passthroughProp(ChildComponent),
 *     differentNameForDef: passthroughProp(ChildComponent, 'def'),
 * }
 * @returns {PropTypes.Validator} A custom validator that validates against the {@link Component}'s props
 */
export function passthroughProp(Component, overridePropName = null) {
  return function customValidator(props, propName, ...rest) {
    const componentPropName = overridePropName || propName;
    const childComponentValidator = _.get(Component, ['propTypes', componentPropName], PropTypes.any);
    return childComponentValidator(props, componentPropName, ...rest);
  };
}
